import { api } from './client';

export interface SisCreditNoteItem {
  itemId: number;
  itemCode: string;
  itemName: string;
  units: number;
  variance: number;
  component?: 'commission' | 'price' | null;
  txnId?: string | null; // store bill no
  invoiceNo?: string | null; // source stock-lot invoice (FIFO)
  costRate?: number | null; // oldest-invoice rate
  sellRate?: number | null; // realised per-unit sell
}

export interface SisCreditNote {
  id: number;
  noteNo: string;
  type: string; // 'Credit' | 'Debit'
  category: string | null; // 'Commission' | 'Difference'
  partyId: number;
  storeName: string;
  group: string;
  periodStart: string;
  periodEnd: string;
  amount: number;
  status: string;
  createdAt: string;
  createdBy: string | null;
  items: SisCreditNoteItem[];
}

export interface SisCreditNoteSide {
  amount: number;
  lines: SisCreditNoteItem[];
}

export interface SisCreditNotePreview {
  partyId: number;
  storeName: string;
  group: string;
  commissionPct: number;
  debit: SisCreditNoteSide;
  credit: SisCreditNoteSide;
}

export interface GenerateCreditNoteResult {
  commission: SisCreditNote | null;
  difference: SisCreditNote | null;
}

export async function listSisCreditNotes(filter: { partyId?: number; type?: string } = {}): Promise<SisCreditNote[]> {
  const r = await api.get<SisCreditNote[]>('/sis-credit-note', { params: filter });
  return r.data;
}

export async function previewSisCreditNote(partyId: number, from: string, to: string): Promise<SisCreditNotePreview> {
  const r = await api.get<SisCreditNotePreview>('/sis-credit-note/preview', { params: { partyId, from, to } });
  return r.data;
}

export interface SisCnLine {
  partyId: number;
  storeName: string;
  group: string;
  saleDate: string;
  txnId: string;
  invoiceNo: string;
  itemCode: string;
  itemName: string;
  barcode: string;
  units: number;
  netValue: number;
  costRate: number;
  sellRate: number;
  priceDiff: number;
  commissionPct: number;
  commission: number;
  noteAmount: number;
  // Store-settlement profit view
  mrpAmt: number;
  discount: number;
  sellingGross: number;
  sellingNet: number;
  sellingGst: number;
  payableInclGst: number;
  itemCost: number;
  netProfit: number;
  netProfitPerPc: number;
  netProfitPct: number;
}

export interface SisCnLinesResult {
  lines: SisCnLine[];
  totals: {
    units: number; net: number; commission: number; priceDiff: number; noteAmount: number;
    sellingGst: number; sellingNet: number; payableInclGst: number; itemCost: number; netProfit: number;
    mrpAmt: number; discount: number;
  };
}

export async function listSisCreditNoteLines(filter: { partyId?: number; group?: string; from?: string; to?: string } = {}): Promise<SisCnLinesResult> {
  const r = await api.get<SisCnLinesResult>('/sis-credit-note/lines', { params: filter });
  return r.data;
}

export interface SisFifoItemRow {
  itemId: number;
  itemCode: string;
  itemName: string;
  barcode: string;
  txnId: string;
  invoiceNo: string;
  qty: number;
  fifoCost: number;
  sellValue: number;
  avgFifoRate: number;
  avgSellRate: number;
  diff: number;
}

export interface SisFifoResult {
  rows: SisFifoItemRow[];
  totals: { qty: number; fifoCost: number; sellValue: number; diff: number };
}

export async function listSisFifoByItem(filter: { partyId?: number; group?: string; from?: string; to?: string } = {}): Promise<SisFifoResult> {
  const r = await api.get<SisFifoResult>('/sis-credit-note/fifo-by-item', { params: filter });
  return r.data;
}

/** Path for the Profitability Excel export (scoped by the current filter). */
export function sisCreditNoteLinesExportPath(filter: { partyId?: number; group?: string; from?: string; to?: string } = {}): string {
  const p = new URLSearchParams();
  if (filter.partyId) p.set('partyId', String(filter.partyId));
  if (filter.group) p.set('group', filter.group);
  if (filter.from) p.set('from', filter.from);
  if (filter.to) p.set('to', filter.to);
  const qs = p.toString();
  return `/sis-credit-note/lines/export.xlsx${qs ? `?${qs}` : ''}`;
}

/** Path for the Notes register Excel export (Notes summary + line items). */
export function sisCreditNotesExportPath(filter: { partyId?: number; type?: string } = {}): string {
  const p = new URLSearchParams();
  if (filter.partyId) p.set('partyId', String(filter.partyId));
  if (filter.type) p.set('type', filter.type);
  const qs = p.toString();
  return `/sis-credit-note/export.xlsx${qs ? `?${qs}` : ''}`;
}

/** Path for the FIFO Cost-vs-Sell by-item Excel export (scoped by the current filter). */
export function sisFifoByItemExportPath(filter: { partyId?: number; group?: string; from?: string; to?: string } = {}): string {
  const p = new URLSearchParams();
  if (filter.partyId) p.set('partyId', String(filter.partyId));
  if (filter.group) p.set('group', filter.group);
  if (filter.from) p.set('from', filter.from);
  if (filter.to) p.set('to', filter.to);
  const qs = p.toString();
  return `/sis-credit-note/fifo-by-item/export.xlsx${qs ? `?${qs}` : ''}`;
}

export async function generateSisCreditNote(payload: {
  partyId: number;
  periodStart: string;
  periodEnd: string;
}): Promise<GenerateCreditNoteResult> {
  const r = await api.post<GenerateCreditNoteResult>('/sis-credit-note/generate', payload);
  return r.data;
}

export interface GenerateGroupCreditNoteResult {
  group: string;
  generated: { store: string; commission: string | null; difference: string | null }[];
  skipped: { store: string; reason: string }[];
}

export async function generateGroupSisCreditNote(payload: {
  group: string;
  periodStart: string;
  periodEnd: string;
}): Promise<GenerateGroupCreditNoteResult> {
  const r = await api.post<GenerateGroupCreditNoteResult>('/sis-credit-note/generate-group', payload);
  return r.data;
}
